/**
 * Server-Sent Events parsing.
 *
 * Kept free of any fetch/auth dependency so the framing rules — which are the
 * fiddly part, since network chunks split frames at arbitrary offsets — can be
 * exercised on their own. See `api.ts` for the transport that feeds this.
 */

/** One event: the `event:` name (if the server sent one) and its `data:` payload. */
export interface StreamEvent {
  event: string | null;
  data: string;
}

/**
 * Locate the frame terminator (a blank line) in `buffer`.
 *
 * Both LF and CRLF appear in the wild — a proxy between the browser and the
 * server may rewrite line endings — so accept either and report which matched.
 */
export function findFrameEnd(buffer: string): { index: number; length: number } | null {
  const lf = buffer.indexOf("\n\n");
  const crlf = buffer.indexOf("\r\n\r\n");
  if (lf === -1 && crlf === -1) return null;
  if (crlf !== -1 && (lf === -1 || crlf < lf)) return { index: crlf, length: 4 };
  return { index: lf, length: 2 };
}

/** Parse one frame's worth of lines. Returns null for comment-only heartbeats. */
export function parseFrame(frame: string): StreamEvent | null {
  const lines = frame.split(/\r?\n/);
  let event: string | null = null;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) continue; // blank, or a comment/heartbeat
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      // Exactly one optional space after the colon belongs to the framing.
      const value = line.slice(5);
      dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
    }
  }

  if (dataLines.length === 0 && event === null) return null;
  return { event, data: dataLines.join("\n") };
}

/**
 * Incrementally turn decoded text chunks into events.
 *
 * Feed whatever arrives — frames straddling chunk boundaries are buffered
 * until complete. Call `flush()` once the body ends to emit a trailing frame
 * that arrived without its terminating blank line.
 */
export function createSSEParser() {
  let buffer = "";

  return {
    push(chunk: string): StreamEvent[] {
      buffer += chunk;
      const events: StreamEvent[] = [];
      let separator = findFrameEnd(buffer);
      while (separator) {
        const frame = buffer.slice(0, separator.index);
        buffer = buffer.slice(separator.index + separator.length);
        const parsed = parseFrame(frame);
        if (parsed) events.push(parsed);
        separator = findFrameEnd(buffer);
      }
      return events;
    },
    flush(): StreamEvent[] {
      const rest = buffer;
      buffer = "";
      const parsed = parseFrame(rest);
      return parsed ? [parsed] : [];
    },
  };
}
